import { useCreateOrUpdateSculptureEditionInBibliography } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import {
  applyFieldConfigOverrides,
  FormLayoutElement,
} from "@/config/forms/general";
import { SculptureEditionInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

const SculptureEditionInBibliographyDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: SculptureEditionInBibliography;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const vt = useVisibleT();
  const defaultFields: FormLayoutElement[] = [
    {
      name: "sculptureEditionDansBibliographieReproduitPage",
      type: "text",
      label: vt("bo.links.reproduitPage", "Reproduit page"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieReproduitPlanche",
      type: "text",
      label: vt("bo.links.reproduitPlanche", "Reproduit planche"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographiePlancheNonNumerotee",
      type: "checkbox",
      label: vt("bo.links.plancheNonNumerotee", "Planche non numérotée"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieCitePage",
      type: "text",
      label: vt("bo.links.citePage", "Cité page"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieListePage",
      type: "text",
      label: vt("bo.links.listePage", "Liste page"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieSousNumero",
      type: "text",
      label: vt("bo.links.sousNumero", "Sous numéro"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographiePhotographe",
      type: "text",
      label: vt("bo.links.photographe", "Photographe"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieNonPagine",
      type: "checkbox",
      label: vt("bo.links.nonPagine", "Non paginé"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieReproductionCouverture",
      type: "checkbox",
      label: vt("bo.links.reproductionCouverture", "Reproduction couverture"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieReproductionCouleur",
      type: "checkbox",
      label: vt("bo.links.reproductionCouleur", "Reproduction couleur"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieTexteLibre",
      type: "text",
      label: vt("bo.links.texteLibre", "Texte libre"),
      className: "w-full",
    } as FormLayoutElement,
    {
      name: "sculptureEditionDansBibliographieNotes",
      type: "richtext",
      label: vt("bo.links.notes", "Notes"),
      className: "w-full",
    } as FormLayoutElement,
  ];
  const schema = z.object({
    sculptureEditionDansBibliographieReproduitPage: z.string().optional(),
    sculptureEditionDansBibliographieReproduitPlanche: z.string().optional(),
    sculptureEditionDansBibliographiePlancheNonNumerotee: z.boolean().optional(),
    sculptureEditionDansBibliographieCitePage: z.string().optional(),
    sculptureEditionDansBibliographieListePage: z.string().optional(),
    sculptureEditionDansBibliographieSousNumero: z.string().optional(),
    sculptureEditionDansBibliographiePhotographe: z.string().optional(),
    sculptureEditionDansBibliographieNonPagine: z.boolean().optional(),
    sculptureEditionDansBibliographieReproductionCouverture: z
      .boolean()
      .optional(),
    sculptureEditionDansBibliographieReproductionCouleur: z.boolean().optional(),
    sculptureEditionDansBibliographieTexteLibre: z.string().optional(),
    sculptureEditionDansBibliographieNotes: z.string().optional(),
    bibliography: z
      .object({
        bibliographieDescriptionNumeroBibliographie: z.string().optional(),
        bibliographieDescriptionResumeLong: z.string().optional(),
      })
      .optional(),
    sculptureEdition: z
      .object({
        sculptureEditionNumeroEnregistrement: z.string().optional(),
        sculptureEditionNotes: z.string().optional(),
      })
      .optional(),
  });

  const { mutate: createOrUpdate } =
    useCreateOrUpdateSculptureEditionInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      bibliography: data.bibliography,
      sculptureEdition: data.sculptureEdition,
      sculptureEditionDansBibliographieReproduitPage:
        data.sculptureEditionDansBibliographieReproduitPage,
      sculptureEditionDansBibliographieReproduitPlanche:
        data.sculptureEditionDansBibliographieReproduitPlanche,
      sculptureEditionDansBibliographiePlancheNonNumerotee:
        data.sculptureEditionDansBibliographiePlancheNonNumerotee,
      sculptureEditionDansBibliographieCitePage:
        data.sculptureEditionDansBibliographieCitePage,
      sculptureEditionDansBibliographieListePage:
        data.sculptureEditionDansBibliographieListePage,
      sculptureEditionDansBibliographieSousNumero:
        data.sculptureEditionDansBibliographieSousNumero,
      sculptureEditionDansBibliographiePhotographe:
        data.sculptureEditionDansBibliographiePhotographe,
      sculptureEditionDansBibliographieNonPagine:
        data.sculptureEditionDansBibliographieNonPagine,
      sculptureEditionDansBibliographieReproductionCouverture:
        data.sculptureEditionDansBibliographieReproductionCouverture,
      sculptureEditionDansBibliographieReproductionCouleur:
        data.sculptureEditionDansBibliographieReproductionCouleur,
      sculptureEditionDansBibliographieTexteLibre:
        data.sculptureEditionDansBibliographieTexteLibre ?? "",
      sculptureEditionDansBibliographieNotes:
        data.sculptureEditionDansBibliographieNotes ?? "",
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdate({
      id: data.id,
      sculptureEditionId: data.sculptureEdition.id,
      bibliographyId: data.bibliography.id,
      sculptureEditionDansBibliographieReproduitPage:
        formData.sculptureEditionDansBibliographieReproduitPage,
      sculptureEditionDansBibliographieReproduitPlanche:
        formData.sculptureEditionDansBibliographieReproduitPlanche,
      sculptureEditionDansBibliographiePlancheNonNumerotee:
        formData.sculptureEditionDansBibliographiePlancheNonNumerotee,
      sculptureEditionDansBibliographieCitePage:
        formData.sculptureEditionDansBibliographieCitePage,
      sculptureEditionDansBibliographieListePage:
        formData.sculptureEditionDansBibliographieListePage,
      sculptureEditionDansBibliographieSousNumero:
        formData.sculptureEditionDansBibliographieSousNumero,
      sculptureEditionDansBibliographiePhotographe:
        formData.sculptureEditionDansBibliographiePhotographe,
      sculptureEditionDansBibliographieNonPagine:
        formData.sculptureEditionDansBibliographieNonPagine,
      sculptureEditionDansBibliographieReproductionCouverture:
        formData.sculptureEditionDansBibliographieReproductionCouverture,
      sculptureEditionDansBibliographieReproductionCouleur:
        formData.sculptureEditionDansBibliographieReproductionCouleur,
      sculptureEditionDansBibliographieTexteLibre:
        formData.sculptureEditionDansBibliographieTexteLibre,
      sculptureEditionDansBibliographieNotes:
        formData.sculptureEditionDansBibliographieNotes,
    });
  };

  const isFromBibliography = type === "bibliography";
  const previewConfig = isFromBibliography
    ? {
        attachments: data.sculptureEdition?.attachments ?? [],
        type: "sculptureEdition" as const,
        alt:
          data.sculptureEdition?.sculptureEditionNumeroEnregistrement ??
          data.sculptureEdition?.sculptureEditionNumeroInventaire,
      }
    : {
        attachments: data.bibliography?.attachments ?? [],
        type: "bibliography" as const,
        alt:
          data.bibliography?.bibliographieDescriptionNumeroBibliographie ??
          data.bibliography?.bibliographieDescriptionTitre,
      };

  const layoutFields =
    fields && fields.length > 0 ? fields : defaultFields;
  const enhancedFields = applyFieldConfigOverrides(layoutFields, {
    sculptureEditionDansBibliographieTexteLibre: {
      expandOnOverflow: true,
    },
  });

  return (
    <LinksDrawerLayout
      form={form}
      fields={enhancedFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default SculptureEditionInBibliographyDrawer;
